/**
 * `loader` del Inicio — ADR-0012, paso 2, mitad 2.
 *
 * Tercera ruta con loader, y la de mayor alcance: es la pantalla que se abre al entrar al panel.
 *
 * ⭐ Acá el loader casi no cuesta nada al arranque, a diferencia de Ventas (+12 KB). El motivo es
 * que la clave, el rango y el mapeo ya vivían en `dashboardQuery.ts` —un módulo chico y sin UI—
 * porque la precarga del Sidebar los necesitaba. Cuando la definición de una consulta ya está fuera
 * de la pantalla, ponerle un loader es gratis. Es un argumento a favor de extraerlas.
 *
 * El porqué de cada decisión está explicado una sola vez, en `./productos.ts`: `ensureQueryData` y no
 * `fetchQuery`, no esperar la promesa, una única definición compartida, y de dónde sale el tenant.
 *
 * 🪤 Este loader y `prefetchDashboardStats` (el que dispara el Sidebar a los 0,5 s) hacen lo mismo
 * sobre la MISMA clave, y eso está bien: los dos usan `ensureQueryData`/`prefetchQuery`, que no
 * hacen nada si la entrada está fresca. El que llegue primero paga; el otro sale gratis. Cubren
 * casos distintos: el loader, entrar directo por URL o volver al Inicio; la precarga, tenerlo listo
 * ANTES de que el usuario haga clic desde otra pantalla.
 */

import { queryClient } from '@/lib/queryClient';
import {
  claveDashboard,
  mapearRpcDashboard,
  rangoPorDefectoDashboard,
} from '@/lib/dashboardQuery';
import { supabase } from '@/lib/supabase';
import { tenantDelPanel } from './tenantDelPanel';

export function cargarInicio() {
  const tenantId = tenantDelPanel();
  if (!tenantId) return null;

  const { start, end } = rangoPorDefectoDashboard();
  const canal = 'all';

  // 🚨 `void` + `return null`: se dispara la carga y NO se la espera. React Router ESPERA el valor
  // que devuelve el loader, así que devolver la promesa congelaba la navegación — el usuario se
  // quedaba en la pantalla anterior. Ver el porqué completo en `./productos.ts`.
  void queryClient.ensureQueryData({
    queryKey: claveDashboard(tenantId, start, end, canal),
    staleTime: 45_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_dashboard_stats', {
        p_tenant_id: tenantId,
        p_start_date: start.toISOString(),
        p_end_date: end.toISOString(),
        p_channel: canal,
      });
      // 🪤 Si el RPC falla se LANZA, no se devuelve un panel en cero: sembrar ventas 0 como si fuera
      // el dato real es peor que no sembrar nada — la pantalla mostraría un negocio sin ventas.
      if (error || !data || typeof data !== 'object') {
        throw error ?? new Error('El RPC del dashboard no devolvió datos');
      }
      const { stats, sparkline } = mapearRpcDashboard(data as Record<string, unknown>);
      // Las tres listas van vacías: el camino RPC no las usa (la pantalla las llena sólo cuando cae
      // al camino alternativo, que el loader no cubre a propósito — es el caso raro).
      return { stats, sparkline, orders: [], products: [], customers: [] };
    },
  }).catch(() => {});
  return null;
}
